import { Map } from 'immutable';
import PropTypes from 'prop-types';
import { createContext, Fragment, useEffect } from 'react';
import Loadable from 'react-loadable';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import r from 'render-dom';
import styled from 'styled-components';

import AccountSetup from 'components/shared/AccountSetup';
import Spinner from 'components/shared/Spinner';
import Loading from 'components/ui/Loading';
import { useMeContext, usePlanContext, useToggle } from 'hooks';
import { scrollToTop } from 'utils/helpers';
import { authHttp } from 'utils/http';
import { trackUser } from 'utils/track';

import ExtensionUpgrade from './ExtensionUpgrade';

const loading = Loading;

const Admin = Loadable({ loader: () => import('./Admin'), loading });
const Dashboard = Loadable({ loader: () => import('./Dashboard'), loading });
const Campaign = Loadable({ loader: () => import('./Campaign'), loading });
const Contacts = Loadable({ loader: () => import('./Contacts'), loading });
const Data = Loadable({ loader: () => import('./Data'), loading });
const DeepWords = Loadable({ loader: () => import('./DeepWords'), loading });
const KeywordDetail = Loadable({ loader: () => import('./KeywordDetail'), loading });
const FeatureProduct = Loadable({ loader: () => import('./Feature'), loading });
const Flags = Loadable({ loader: () => import('./Flags'), loading });
const Help = Loadable({ loader: () => import('./Help'), loading });
const MarketData = Loadable({ loader: () => import('./MarketData'), loading });
const MarketReport = Loadable({ loader: () => import('./MarketData/Report'), loading });
const Notifications = Loadable({ loader: () => import('./Notifications'), loading });
const Rebate = Loadable({ loader: () => import('./Rebate'), loading });
const Rebates = Loadable({ loader: () => import('./Rebates'), loading });
const Reports = Loadable({ loader: () => import('./Reports'), loading });
const Settings = Loadable({ loader: () => import('./Settings'), loading });
const Peek = Loadable({ loader: () => import('./Peek'), loading });
const ReverseAsin = Loadable({ loader: () => import('./ReverseAsin'), loading });
const RewardCampaign = Loadable({ loader: () => import('./Campaign/RewardCampaign'), loading });
const MarketResearch = Loadable({ loader: () => import('./Campaign/MarketResearch'), loading });
const NonAmazonProduct = Loadable({ loader: () => import('./NonAmazonProduct'), loading });
const Wizards = Loadable({ loader: () => import('../public/Wizards'), loading });

const AppContainer = styled.div.attrs({ className: 'peek-overflow' })`
    box-sizing: border-box;
    margin-left: ${({ isLoggedIn }) => isLoggedIn ? '110px' : '0'};
    overflow-x: hidden;
    overflow-y: auto;

    @media screen and (max-width: 768px) {
        margin-left: 0;
    }
`;

function loadZendesk(me) {
    /* eslint-disable */
    var ua = navigator.userAgent.toLowerCase(), platform = navigator.platform.toLowerCase();
    var platformName = ua.match(/ip(?:ad|od|hone)/) ? 'ios' : (ua.match(/(?:webos|android)/) || platform.match(/mac|win|linux/) || ['other'])[0];
    var isMobile = /ios|android|webos/.test(platformName);

    if (!isMobile) {
        window.zESettings = {
            webWidget: {
                color: { theme: '#000000', launcher: '#F9F9F9', launcherText: '#000000' },
                launcher: {
                    labelVisible: false,
                    mobile: { labelVisible: false },
                },
                offset: { horizontal: '-20px' },
                position: { horizontal: 'left', vertical: 'bottom' }
            }
        };
        window.zEmbed || function(e,t){
            let n,o,d,i,s,a=[],r=document.createElement("iframe");
            window.zEmbed=function(){
                a.push(arguments)
            },
            window.zE=window.zE || window.zEmbed,
            r.src="javascript:false",
            r.title="",
            r.role="presentation",
            (r.frameElement||r).style.cssText="display: none",
            d=document.getElementsByTagName("script"),
            d=d[d.length-1],
            d.parentNode.insertBefore(r,d),
            i=r.contentWindow,
            s=i.document;
            try{
                o=s
            }catch(e){
                n=document.domain,
                r.src='javascript:var d=document.open();d.domain="'+n+'";void(0);',
                o=s
            }o.open()._l=function(){
                var e=this.createElement("script");
                n&&(this.domain=n),
                e.id="js-iframe-async",
                e.src="https://assets.zendesk.com/embeddable_framework/main.js",
                this.t=+new Date,
                this.zendeskHost="snagshout.zendesk.com",
                this.zEQueue=a,
                this.body.appendChild(e),
                window.zE(function() {
                    zE.identify({
                        name: me.get('fullName'),
                        email: me.get('email')
                    });
                })
            },
            o.write(
                '<body onload="document._l();">'
            ),o.close()
        }();
    }
    /* eslint-enable */
}

const propTypes = {
    children: PropTypes.node,
};

export const SubscriptionModalContext = createContext();

export default function App() {
    const { pathname } = useLocation();
    const [me, refreshMe, isFetchingMe] = useMeContext();
    const [plan,, plans, isFetchingPlans] = usePlanContext();
    const [showSubModal, toggleSubModal] = useToggle(false);
    const company = me.get('company', Map());
    const isAdmin = me.get('isAdmin');
    const isCampaignCreation = pathname.includes('/campaigns/')
        || pathname.includes('/landing-page/');
    const isWizard = pathname.includes('/wizard');
    const isSubscribed = company.get('isSubscribed');
    const activeSubscription = company.get('activeSubscription');
    const hasReviewAutoPlay = me.getIn(['metadata', 'hasReviewAutoPlay'], false);

    useEffect(() => {
        if (!me.get('id') && !isWizard && !isFetchingMe) {
            refreshMe();
        } else {
            trackUser(me);
        }
    }, [me]);

    useEffect(() => { scrollToTop(); }, [pathname]);

    if (
        isSubscribed
        && plan.get('chat')
        && !isCampaignCreation
    ) {
        loadZendesk(me);
    }

    function contactSupport() {
        // eslint-disable-next-line no-alert
        alert(
            `There's an issue with your account. Please contact
            help@snagshout.com`
        );
    }

    if (me.get('id') && !isSubscribed && !activeSubscription && !me.get('isAdmin') && plans.size && !isFetchingPlans) {
        const freePlan = plans.find((p) => p.get('price') === 0);
        if (freePlan) {
            authHttp.post('/subscriptions', {
                stripePlan: freePlan.get('planId')
            })
                .then(() => {
                    window.location.reload();
                });
        } else {
            contactSupport();
        }


        return (
            r(Spinner, { color: '#000' }, [
                r.p(['One moment']),
            ])
        );
    }

    return (
        r(SubscriptionModalContext.Provider, { value: toggleSubModal }, [
            r(AppContainer, { isLoggedIn: me.get('id') }, [
                r(AccountSetup, { onClose: toggleSubModal, show: showSubModal }),
                r(Fragment, { isRendered: me.get('id') }, [
                    r(Switch, [
                        r(Route, { component: Wizards, path: '/campaign/offer/:id' }),
                        r(Route, { component: Campaign, path: '/campaign/:id' }),
                        r(Route, { component: Campaign, path: '/facebook-campaign/:id' }),
                        r(Route, { component: Campaign, path: '/landing-page/:id' }),
                        r(Route, { component: Campaign, path: '/review-survey/:id' }),
                        r(Route, { component: Campaign, path: '/lifestyle-photo/:id' }),
                        r(Route, { component: Campaign, path: '/video-review/:id' }),
                        r(Route, { component: Campaign, path: '/testimonial/:id' }),
                        r(Route, { component: Campaign, path: '/social-media/:id' }),
                        r(Route, { component: Campaign, path: '/coupon-code/:id' }),
                        r(Route, { component: Campaign, path: '/classic-rebate/:id' }),
                        r(Route, { component: Campaign, path: '/video-review/:id' }),
                        r(Route, { component: RewardCampaign, path: '/reward-campaign/:id' }),
                        r(Route, { component: MarketResearch, path: '/market-research/:id' }),
                        r(Route, { component: Dashboard, path: '/dashboard' }),
                        r(Route, { component: FeatureProduct, path: '/featured-product/:campaignId' }),
                        r(Route, { component: Contacts, path: '/contacts' }),
                        r(Route, { component: Flags, path: '/flags/:campaignId' }),
                        r(Route, { component: Flags, path: '/flags' }),
                        r(Route, { component: Help, path: '/help' }),
                        r(Route, { component: Notifications, path: '/notifications' }),
                        r(Route, { component: Rebate, path: '/rebates/:id' }),
                        r(Route, { component: Rebates, path: '/rebates' }),
                        r(Route, { component: Reports, isRendered: isAdmin, path: '/reports' }),
                        r(Route, { component: Settings, path: '/settings' }),
                        r(Route, { component: Data, path: '/data' }),
                        r(Route, { component: Peek, path: '/peek' }),
                        r(Route, { component: ReverseAsin, path: '/reverse-asin' }),
                        r(Route, { component: DeepWords, path: '/deep-words' }),
                        r(Route, { component: KeywordDetail, path: '/keyword/:keyword+' }),
                        r(Route, { component: MarketData, exact: true, path: '/market-data' }),
                        r(Route, { component: MarketReport, path: '/market-data/:id' }),
                        r(Redirect, { from: '/product/new', strict: true, to: '/wizard/product' }),
                        r(Route, { component: NonAmazonProduct, path: '/product/:id' }),
                        r(Route, {
                            component: ExtensionUpgrade,
                            isRendered: !isSubscribed && hasReviewAutoPlay,
                            path: '/',
                        }),
                        r(Route, { component: Admin, isRendered: isAdmin, path: '/admin/:path' }),
                        r(Route, {
                            component: Dashboard,
                            exact: true,
                            path: '/',
                        }),
                        r(Redirect, { from: '/keyword', strict: true, to: '404' }),
                        r(Redirect, { from: '*', to: '404' }),
                    ]),
                ]),
            ]),
        ])
    );
}

App.propTypes = propTypes;
